import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useAssignUserRolesMutation,
  useRolesQuery,
} from '../../../hooks/useDashboardQueries';
import { usePermissions } from '../../../hooks/usePermissions';
import { ConfirmChangeDialog } from './components/ConfirmChangeDialog';

interface UserRolesCardProps {
  userId: string;
  initialRoleIds: string[];
}

export const UserRolesCard = memo(function UserRolesCard({
  userId,
  initialRoleIds,
}: UserRolesCardProps) {
  const { t } = useTranslation();
  const { has } = usePermissions();
  const canManage = has('users.manage');

  const { data: roles, isLoading } = useRolesQuery();
  const mutation = useAssignUserRolesMutation(userId);

  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(initialRoleIds),
  );
  const [error, setError] = useState<string | undefined>();
  const [isConfirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    setSelected(new Set(initialRoleIds));
  }, [initialRoleIds]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isDirty = useMemo(() => {
    const original = new Set(initialRoleIds);
    if (original.size !== selected.size) return true;
    for (const id of selected) if (!original.has(id)) return true;
    return false;
  }, [initialRoleIds, selected]);

  const handleReset = () => {
    setError(undefined);
    setSelected(new Set(initialRoleIds));
  };

  const handleSave = async () => {
    setConfirmOpen(false);
    setError(undefined);
    try {
      await mutation.mutateAsync({ roleIds: Array.from(selected) });
    } catch (e) {
      const msg =
        (e as { response?: { data?: { message?: string } }; message?: string })
          ?.response?.data?.message ?? (e as Error).message;
      setError(msg);
    }
  };

  if (!canManage) return null;

  const nameOf = (id: string) => roles?.find((r) => r.id === id)?.name ?? id;
  const list = (ids: string[]) => ids.map(nameOf).join(', ') || t('dashboard.userDetails.confirm.none');
  const before = initialRoleIds;
  const after = Array.from(selected);
  const added = after.filter((id) => !before.includes(id));
  const removed = before.filter((id) => !selected.has(id));
  const diffNote = [
    added.length ? `${t('dashboard.userDetails.confirm.added')}: ${list(added)}` : '',
    removed.length ? `${t('dashboard.userDetails.confirm.removed')}: ${list(removed)}` : '',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <section className="ud-panel" aria-labelledby="user-roles-title">
      <h2 id="user-roles-title" className="ud-panel-title">
        {t('dashboard.userDetails.rolesTitle')}
      </h2>

      {isLoading || !roles ? (
        <p className="ud-panel-hint">{t('dashboard.userDetails.rolesLoading')}</p>
      ) : (
        <>
          <div className="ud-role-list" role="group" aria-labelledby="user-roles-title">
            {roles.map((role) => {
              const on = selected.has(role.id);
              return (
                <button
                  key={role.id}
                  type="button"
                  aria-pressed={on}
                  className={`ud-role-chip${on ? ' is-on' : ''}`}
                  onClick={() => toggle(role.id)}
                >
                  {on && (
                    <span className="material-symbols-outlined" aria-hidden="true">check</span>
                  )}
                  {role.name}
                </button>
              );
            })}
          </div>
          <p className="ud-panel-hint">{t('dashboard.userDetails.rolesHint')}</p>

          {error && <p className="ud-panel-error" role="alert">{error}</p>}

          {isDirty && (
            <div className="ud-panel-footer">
              <button
                type="button"
                className="ud-btn"
                onClick={handleReset}
                disabled={mutation.isPending}
              >
                {t('dashboard.userDetails.cancel')}
              </button>
              <button
                type="button"
                className="ud-btn ud-btn-primary"
                onClick={() => setConfirmOpen(true)}
                disabled={mutation.isPending || selected.size === 0}
              >
                {mutation.isPending
                  ? t('dashboard.roles.form.saving')
                  : t('dashboard.userDetails.saveRoles')}
              </button>
            </div>
          )}

          <ConfirmChangeDialog
            open={isConfirmOpen}
            message={t('dashboard.userDetails.confirm.rolesMessage', {
              from: list(before),
              to: list(after),
            })}
            note={diffNote}
            confirmLabel={t('dashboard.userDetails.saveRoles')}
            onCancel={() => setConfirmOpen(false)}
            onConfirm={() => void handleSave()}
          />
        </>
      )}
    </section>
  );
});

export default UserRolesCard;
